type Turn = { role?: string; content?: unknown; isStreaming?: unknown; tool_calls?: unknown; conceptsRun?: unknown; playbookProgress?: unknown };
type Direction = { label: string; description: string };
export type AdTypeEachFollowup = { count: number; perType: number; directions: Direction[]; brief: string };

const wordCounts: Record<string, number> = {one:1,two:2,three:3,four:4,five:5,six:6,seven:7,eight:8,nine:9,ten:10,twenty:20};
const textOf = (content: unknown): string => typeof content === 'string' ? content : Array.isArray(content)
  ? content.flatMap(block => block?.type === 'text' && typeof block.text === 'string' ? [block.text] : []).join('\n') : '';
const plain = (value: string) => value.replace(/\*\*|__/g, '').trim();

/** Resolve a current execution request against one immediately preceding list
 * of ad directions. Closing recommendations do not silently narrow "each".
 * This is creative context only; it does not accept instructions from tools,
 * completed workflows, historical lists, or an unrelated shopping/checklist. */
export function resolveAdTypeEachFollowup(message: string, history: Turn[] = []): AdTypeEachFollowup | null {
  const command = message.trim().replace(/^(?:please\s+)?(?:(?:can|could|would|will)\s+you\s+)?(?:please\s+)?/i, '');
  const match = /^(?:make|create|generate|render|build|produce)\s+(?:me\s+)?(\d{1,3}|one|two|three|four|five|six|seven|eight|nine|ten|twenty)\s+(?:ads?\s+)?(?:of\s+)?each(?:\s+(?:(?:ad\s+)?(?:type|kind|format|direction)|of\s+(?:these|those|them)))?(?:\s+(?:now|please))?[.!?]*$/i.exec(command);
  if (!match) return null;
  const perType = Number(match[1]) || wordCounts[match[1].toLowerCase()];
  if (!Number.isSafeInteger(perType) || perType < 1) return null;

  const turns = history.filter(turn => turn.role === 'user' || turn.role === 'assistant');
  if (turns.at(-1)?.role === 'user' && textOf(turns.at(-1)?.content).trim() === message.trim()) turns.pop();
  const previous = turns.at(-1);
  if (previous?.role !== 'assistant' || previous.isStreaming || previous.conceptsRun || previous.playbookProgress ||
      Array.isArray(previous.tool_calls) && previous.tool_calls.length) return null;
  const reply = textOf(previous.content).trim();
  const precedingUser = turns.at(-2);
  const userText = precedingUser?.role === 'user' ? textOf(precedingUser.content) : '';
  if (!/\b(?:ads?|advertisements?|creatives?)\b/i.test(userText) && !/\b(?:ads?|advertisements?|creatives?)\b/i.test(reply)) return null;

  // One flat list is unambiguous. Multiple lists or nested options require the
  // conversational route instead of silently selecting one of them.
  const blocks: Direction[][] = [];
  let active: Direction[] | undefined;
  for (const line of reply.split(/\r?\n/)) {
    if (!line.trim()) continue;
    const bullet = /^(?:[-*•]\s+|\d{1,2}[.)]\s+)(.+)$/.exec(line);
    if (!bullet) { active = undefined; continue; }
    const content = plain(bullet[1]);
    const colon = content.indexOf(':');
    const label = (colon < 0 ? content : content.slice(0, colon)).trim();
    const description = colon < 0 ? '' : content.slice(colon + 1).trim();
    if (!label || label.length > 100 || label.split(/\s+/).length > 8) return null;
    if (!active) { active = []; blocks.push(active); }
    active.push({ label, description });
  }
  if (blocks.length !== 1 || blocks[0].length < 2 || blocks[0].length > 20) return null;
  const directions = blocks[0];
  if (new Set(directions.map(item => item.label.toLowerCase())).size !== directions.length) return null;
  // The list must actually offer creative directions, not just steps or
  // product names inside an otherwise ad-related conversation.
  const adLabels = directions.filter(item => /\b(?:ads?|advertisements?|creatives?|demos?|before[\s–—-]*(?:and[\s–—-]*)?after|close[- ]?ups?|carousels?|lifestyle|problem[\s–—-]*solution|testimonials?|UGC|comparisons?|how[- ]to|product\s+shots?)\b/i.test(item.label));
  if (adLabels.length < 2 || adLabels.length < Math.ceil(directions.length / 2)) return null;
  const count = perType * directions.length;
  const allocation = directions.map(({label, description}) => {
    // Explicit output nouns let the existing allocation parser retain quotas
    // for noncanonical formats such as application demos and how-to carousels.
    const format = label.replace(/[–—]/g, '-');
    return `- ${perType} ${/\bads?$/i.test(format) ? format : `${format} ads`}: ${description}`.trimEnd();
  }).join('\n');
  return {
    count, perType, directions,
    brief: `Make ${count} ads. Create exactly ${perType} for EVERY direction in the previously suggested list, preserving each direction's description. The total is ${count}; do not narrow the list to the closing recommendation.\nRequired direction allocation:\n${allocation}\nCurrent user request: ${message.trim()}`,
  };
}
